import { ShareDialogAccessLevel } from "@components/access-level/share-dialog-access-level";
import { CopyLinkButton } from "@components/common/button/copy-link-button";
import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/dialog-constants";
import { SphereActionDivider } from "@components/common/sphere-action-divider";
import { Alert, Dialog, Skeleton, Divider } from "@flat-ui";
import { useHasUserValidPermissionProjectLevel } from "@hooks/permission-control/use-has-user-valid-permission-project-level";
import { defaultProjectLaunchTargetSelector } from "@store/sdb-company/sdb-company-selector";
import { isSelectedProjectEditableSelector, selectedProjectSelector } from "@store/projects/projects-selector";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { getOpenProjectHref, getProjectArchivingState } from "@utils/project-utils";
import { UtilsTypes } from "@stellar/utils";
import { ProjectEvents } from "@utils/track-event/track-event-list";
import { useEffect, useState } from "react";
import { InviteMemberToProjectDialog } from "@pages/projects/invite-member-to-project-dialog";
import { ProjectMembersAccordion } from "@pages/projects/project-members-accordion";
import { fetchProjectDetails, fetchProjectFeaturesAvailable } from "@store/projects/projects-slice-thunk";
import { useCompanyId } from "@context-providers/company-id-provider";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
/** Reusable share dialog for a project which includes access level management and member invitations */
export function ProjectShareDialog({
  project,
  isOpen,
  onClose,
  children: triggerButton
}) {
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  const {
    handleErrorWithToast
  } = useErrorContext();
  const {
    defaultOpenProjectTarget
  } = useAppSelector(defaultProjectLaunchTargetSelector);
  const selectedProjectInStore = useAppSelector(selectedProjectSelector);

  // Stream endpoints (e.g. the projects map view) return projects without the
  // "permissions" property, so permission checks against the passed project would
  // always fail. When the fully-loaded project is available in the store we use it
  // instead, so the access level and member sections are evaluated correctly.
  const resolvedProject = selectedProjectInStore?.id === project.id ? selectedProjectInStore : project;
  const isProjectEditable = useAppSelector(isSelectedProjectEditableSelector());
  const {
    canInviteUsersToPrivateProject,
    canViewAllProjectMembers,
    canChangeProjectAccessLevel
  } = useHasUserValidPermissionProjectLevel({
    project: resolvedProject
  });
  const isProjectArchived = getProjectArchivingState(resolvedProject.archivingState) === UtilsTypes.ProjectArchivingState.archived;
  const isAccessLevelEditable = isProjectEditable && canChangeProjectAccessLevel && !isProjectArchived;

  // Whether the project details still need to be fetched to evaluate the
  // permission-gated sections below. Used to show a skeleton placeholder so the
  // dialog layout doesn't jump when the sections appear after fetching.
  const [isLoadingDetails, setIsLoadingDetails] = useState(isOpen && selectedProjectInStore?.id !== project.id);

  // Fetch the project details when the dialog opens for a project whose details
  // (including permissions) aren't loaded yet, so the permission-gated sections
  // below can be evaluated. Skipped when the project is already the selected one
  // to avoid an unnecessary request.
  useEffect(() => {
    if (!isOpen || selectedProjectInStore?.id === project.id) {
      setIsLoadingDetails(false);
      return;
    }
    async function fetchDetails() {
      setIsLoadingDetails(true);
      try {
        await dispatch(fetchProjectDetails({
          companyId,
          projectId: project.id
        })).unwrap();
      } catch (error) {
        handleErrorWithToast({
          id: `ProjectShareDialog-fetchDetails-${Date.now().toString()}`,
          title: "Could not load project permissions",
          error
        });
      } finally {
        setIsLoadingDetails(false);
      }
    }
    void fetchDetails();
  }, [isOpen, selectedProjectInStore?.id, project.id, companyId, dispatch, handleErrorWithToast]);

  // Projects loaded from the projects map have no featuresAvailable, and the project details
  // don't return them either. Without them the role picker falls back to the workspace check,
  // which project admins don't pass.
  const [featuresFetchedForId, setFeaturesFetchedForId] = useState(null);
  const shouldFetchFeatures = isOpen && !isLoadingDetails && selectedProjectInStore?.id === project.id && !selectedProjectInStore.featuresAvailable && canInviteUsersToPrivateProject && !isProjectArchived && featuresFetchedForId !== project.id;
  useEffect(() => {
    if (!shouldFetchFeatures) {
      return;
    }
    async function fetchFeatures() {
      try {
        await dispatch(fetchProjectFeaturesAvailable({
          projectId: project.id
        })).unwrap();
      } catch (error_0) {
        handleErrorWithToast({
          id: `ProjectShareDialog-fetchFeatures-${Date.now().toString()}`,
          title: "Could not load project features",
          error: error_0
        });
      } finally {
        setFeaturesFetchedForId(project.id);
      }
    }
    void fetchFeatures();
  }, [shouldFetchFeatures, project.id, dispatch, handleErrorWithToast]);

  // Some triggers keep the dialog mounted, so allow a failed fetch to be retried on reopening.
  // Reset on opening rather than closing, since a fetch may still fail after the dialog closed.
  // Fetched features are stored on the project, so a successful fetch isn't repeated.
  useEffect(() => {
    if (isOpen) {
      setFeaturesFetchedForId(null);
    }
  }, [isOpen]);
  return <>
      {triggerButton}

      <Dialog open={isOpen} onClose={onClose} title="Share project" topRightActions={<>
            <CopyLinkButton targetHref={getOpenProjectHref({
        openTarget: defaultOpenProjectTarget,
        projectId: project.id
      })} logEventParams={{
        name: ProjectEvents.copyShareProjectLink,
        props: {
          projectId: project.id,
          accessLevel: project.accessLevel,
          location: "share-project-dialog"
        }
      }} />
            <SphereActionDivider />
          </>}>
        <ShareDialogAccessLevel type="project" entity={resolvedProject} isAccessLevelEditable={isAccessLevelEditable} location="project-share-dialog" />

        {isProjectArchived && <Alert variant="info" title="This project is archived. You cannot change its access level or invite members." sx={{
        mt: SPACE_ELEMENTS_OF_MODAL
      }} />}

        {isLoadingDetails || shouldFetchFeatures ? <>
            {!isProjectArchived && <>
                <Divider sx={{
            my: SPACE_ELEMENTS_OF_MODAL
          }} />
                <Skeleton variant="rectangular" height="47px" />
              </>}
            <Divider sx={{
          mt: SPACE_ELEMENTS_OF_MODAL,
          mb: "12px"
        }} />
            <Skeleton variant="rectangular" height="48px" />
          </> : <>
            {!isProjectArchived && canInviteUsersToPrivateProject && <>
                <Divider sx={{
            my: SPACE_ELEMENTS_OF_MODAL
          }} />
                <InviteMemberToProjectDialog project={resolvedProject} />
              </>}

            {canViewAllProjectMembers && <>
                <Divider sx={{
            mt: SPACE_ELEMENTS_OF_MODAL,
            mb: "12px"
          }} />
                <ProjectMembersAccordion project={resolvedProject} location="share-project-dialog" />
              </>}
          </>}
      </Dialog>
    </>;
}